import { css } from "@emotion/react";
import { ImageIcon } from "lucide-react";
import { useEffect, useState } from "react";

// Import Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import {
	Autoplay,
	EffectCards,
	EffectCoverflow,
	EffectFade,
	Navigation,
	Pagination,
} from "swiper/modules";
import type { AutoplayOptions, SwiperModule } from "swiper/types";
import { useProductDetailsState } from "../../../hooks/useProductDetailsState";
import { useStorefrontSettingsState } from "../../../hooks/useStorefrontSettingsState";

const mobileImageSize: number = 320;
const desktopImageSize: number = 512;

/* Image */
export const ImageField = () => {
	const productDetailsState = useProductDetailsState();
	const product = productDetailsState.get().product;
	const images = product?.Images ?? [];
	const [isDesktop, setIsDesktop] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}
		return window.matchMedia(`(min-width: ${desktopImageSize + 32}px)`).matches;
	});
	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}
		const mediaQuery = window.matchMedia(
			`(min-width: ${desktopImageSize + 32}px)`,
		);
		const updateIsDesktop = () => {
			setIsDesktop(mediaQuery.matches);
		};
		updateIsDesktop();
		mediaQuery.addEventListener("change", updateIsDesktop);
		return () => mediaQuery.removeEventListener("change", updateIsDesktop);
	}, []);
	const itemWidth = isDesktop ? desktopImageSize : mobileImageSize;

	const storefrontSettingsState = useStorefrontSettingsState();
	const swiperModules: SwiperModule[] = [];
	let autoplayOptions: AutoplayOptions | undefined;
	const settings = storefrontSettingsState.get().settings;
	if (settings?.AutoplayImagesOnProductDetails) {
		swiperModules.push(Autoplay);
		autoplayOptions = {
			delay: (settings.AutoplaySpeed || 5) * 1000,
		};
	}
	swiperModules.push(Pagination);
	swiperModules.push(Navigation);
	swiperModules.push(EffectFade, EffectCards, EffectCoverflow);
	const imagesTransitionEffect =
		storefrontSettingsState.get().settings?.ImagesTransitionEffect ?? "fade";
	const imagesTransitionSpeed =
		parseFloat(
			storefrontSettingsState.get().settings?.ImagesTransitionSpeed ?? "1000",
		) ?? 1000;
	//const shouldLoop = images.length >= 3;
	const shouldLoop = false;

	if (product) {
		return (
			<div>
				<div
					css={css`
                    display:none;
                    @media print {
                        display: flex;
                        flex-direction: column;
                        justify-content:center;
                    }
                    `}
				>
					{images.map((image, index) => {
						if (index === 0) {
							return (
								<div
									key={0}
									css={css`
                                        display:flex;
                                        align-items:center;
                                        justify-content:center;
                                        flex-wrap: wrap;
                                        `}
								>
									<img
										src={image.Url}
										alt={product.ItemName}
										style={{
											maxWidth: `${desktopImageSize * 0.75}px`,
										}}
									/>
								</div>
							);
						}
						return null;
					})}
					{(!images || images.length === 0) && (
						<div
							key={0}
							css={css`
                                display:flex;
                                align-items:center;
                                justify-content:center;
                                width: ${mobileImageSize}px;
                                height: ${mobileImageSize}px;
                                @media (min-width: ${desktopImageSize}px) {
                                    width: ${desktopImageSize}px;
                                    height: ${desktopImageSize}px;
                                }
                                `}
						>
							<ImageIcon
								css={css`flex:1 1 0;width:256px;height:256px;color:#777777;`}
							/>
						</div>
					)}
				</div>

				<div
					className="noprint"
					css={css`
                        flex: 1 1 0; 
                        display: flex;
                        justify-content:center;
                        `}
				>
					<div
						css={css`
                            flex: 0 0 auto;
                            overflow:hidden;
                            max-width: ${itemWidth}px;
                            max-height: ${itemWidth}px;

							.swiper-button-disabled {
								pointer-events: auto !important;
								cursor: not-allowed;
							}
                        `}
					>
						<Swiper
							speed={imagesTransitionSpeed}
							pagination={{
								dynamicBullets: true,
								type: "bullets",
							}}
							autoplay={autoplayOptions}
							spaceBetween={0}
							navigation={true}
							grabCursor={true}
							centeredSlides={true}
							centerInsufficientSlides={true}
							slidesPerView={1}
							modules={swiperModules}
							loop={shouldLoop}
							effect={imagesTransitionEffect}
							fadeEffect={{
								crossFade: true,
							}}
						>
							{images.map((image) => (
								<SwiperSlide
									key={image.Id}
									style={{
										width: itemWidth,
										height: itemWidth,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<img
										src={image.Url}
										alt=""
										style={{
											maxWidth: itemWidth,
											maxHeight: itemWidth,
											width: "auto",
											height: "auto",
										}}
									/>
								</SwiperSlide>
							))}
							{(!images || images.length === 0) && (
								<SwiperSlide>
									<div
										key={0}
										css={css`
                                        display:flex;
                                        align-items:center;
                                        justify-content:center;
                                        width: ${itemWidth}px;
                                        height: ${itemWidth}px;
                                        `}
									>
										<ImageIcon
											css={css`flex:1 1 0;width:256px;height:256px;color:#777777;`}
										/>
									</div>
								</SwiperSlide>
							)}
						</Swiper>
					</div>
				</div>
			</div>
		);
	} else {
		return null;
	}
};
